<template>
  <Expandable header="Attachments" class="mb-10">
    <div>
      <b-row class="mobile-table-header d-none d-md-flex px-2 py-2 mx-1">
        <b-col cols="12" md="6"> Name </b-col>
        <b-col cols="12" md="6"> File / Response </b-col>
      </b-row>
      <b-row
        class="px-md-4 py-2 d-flex align-items-center"
        v-for="(item, i) in files"
        :key="i"
      >
        <b-col
          cols="12"
          md="6"
          v-html="formattedLabel(item.name, getRules(item.rules, item.name))"
        >
        </b-col>
        <b-col cols="12" md="6">
          <!-- fieldtype 2: Radio Button (Yes/No) -->
          <template v-if="item.fieldtype === 2">
            <yes-no
              v-model="item.radioValue"
              @input="inputRadio(i)"
              :disabled="isDisabled"
              :rules="getRules(item.rules, item.name).includes('required') ? 'required' : ''"
            ></yes-no>
          </template>
          <!-- fieldtype 1 or default: File Upload -->
          <template v-else>
            <div v-if="item.url" class="d-flex mt-2">
              <b-button @click="open(item)" variant="outline-primary" size="sm">
                View
              </b-button>
              <b-button
                class="mx-2"
                v-if="isAdmin && !isDisabled"
                @click="remove(item)"
                variant="outline-danger"
                size="sm"
              >
                Delete
              </b-button>
            </div>
            <file-input
              v-else
              @input="input(i)"
              :disabled="isDisabled"
              :rules="getRules(item.rules, item.name)"
              v-model="item.file"
              accept=".jpg,capture=camera,.jpeg,.png,.pdf"
            ></file-input>
          </template>
        </b-col>
      </b-row>
    </div>
  </Expandable>
</template>

<script>
import { openFile } from "../../../assets/js/common";
import { FIELD } from "../../../assets/js/rules";
import hospitalCreateMixins from "../../../assets/mixins/hospital.create.mixins";
import hospitalService from "../../../assets/services/hospital.service";
import Expandable from "../../Expandable.vue";
import YesNo from "../../form/YesNo.vue";

// Government hospital specific documents (only these 15 shown for hospital_type == 1)
// Names must match exactly with master_fileupload.file_upload_name in the database
const GOVT_HOSPITAL_DOCS = [
  "HFR (Health Facility Registry) Number Proof",
  "PAN Card of Hospital",
  "GST Registration Certificate",
  "NABH/ISO Certificate",
  "Fire Safety Certificate",
  "Biomedical Waste Authorization",
  "NOTTO Registration Certificate",
  "Hospital Trade License Certificate",
  "ABARK Empanelment",
  "Ambulance Facility Details",
  "Laboratory Facility Details",
  "Blood Bank Facility Details",
  "Power Backup Facility Details/Certificate",
  "AERB1 Licenses",
  "Transplant Team Annexure",
  "Brain Death Declaration Committee Annexure"
];

// Mandatory documents for government hospitals
// HFR, PAN, Fire, Biomedical, NOTTO, ABRK, Ambulance, Power Backup, AERB1
const GOVT_MANDATORY_DOCS = [
  "HFR (Health Facility Registry) Number Proof",
  "PAN Card of Hospital",
  "Fire Safety Certificate",
  "Biomedical Waste Authorization",
  "NOTTO Registration Certificate",
  "ABARK Empanelment",
  "Ambulance Facility Details",
  "Power Backup Facility Details/Certificate",
  "AERB1 Licenses",
];

export default {
  props: {
    value: Array,
    hospital: Object,
    filesList: Array,
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: { Expandable, YesNo },
  mixins: [hospitalCreateMixins],
  data() {
    return {
      rules: FIELD,
      files: [],
    };
  },
  computed: {
    master() {
      return this.$store.getters.master;
    },
    isGovernmentHospital() {
      // hospital_type 1 = Government Hospital, 3 = Government Medical College
      return this.hospital && (this.hospital.hospital_type == 1 || this.hospital.hospital_type == 3);
    },
    uploads() {
      if (this.master.master_fileupload instanceof Array == false) return [];
      let docs = this.master.master_fileupload.filter((item) => {
        return item.file_upload_type_id === 1;
      });
      // For Government Hospital, show only the 15 specified documents
      if (this.isGovernmentHospital) {
        docs = docs.filter((item) => {
          return GOVT_HOSPITAL_DOCS.includes(item.file_upload_name);
        });
        // Sort to match the defined order
        docs.sort((a, b) => {
          return (
            GOVT_HOSPITAL_DOCS.indexOf(a.file_upload_name) -
            GOVT_HOSPITAL_DOCS.indexOf(b.file_upload_name)
          );
        });
      }
      return docs;
    },
  },
  watch: {
    uploads() {
      this.init();
    },
  },
  methods: {
    formattedLabel(label, rules) {
      if (!rules) return label;
      if (rules.indexOf("required") != -1)
        return label + " <span style='color:red'>*</span>";
      return label;
    },
    isGovtMandatory(fileName) {
      return GOVT_MANDATORY_DOCS.some(
        (field) => fileName && fileName.includes(field)
      );
    },
    getRules(rules, fileName) {
      // For Government Hospital, override mandatory rules
      if (this.isGovernmentHospital) {
        if (this.isGovtMandatory(fileName)) {
          return "required|file-image-pdf|file-size";
        }
        // Non-mandatory government docs are optional
        return "file-image-pdf|file-size";
      }
      return rules;
    },
    remove({ hospital_license_fileupload_mapping_id }) {
      const vm = this;
      vm.$confirm.show({
        message: "Are you sure you want to delete this attachment?",
        onConfirm: async () => {
          try {
            vm.$loader.show();
            let { data } = await hospitalService.removeFile(
              hospital_license_fileupload_mapping_id
            );
            let { status, msg } = data;
            if (status == false) {
              vm.$alert.show(msg);
            } else {
              vm.$emit("onFileRemoved", hospital_license_fileupload_mapping_id);
            }
            vm.$loader.hide();
          } catch (error) {
            vm.$loader.hide();
            vm.$alert.show(error.message);
          }
        },
      });
    },
    open({ url }) {
      openFile(url);
    },
    init() {
      const vm = this;
      vm.files = vm.uploads.map((item) => {
        let { file_upload_name, file_upload_id, is_required, fieldtype } = item;
        // fieldtype: 1 = file upload, 2 = radio button (yes/no)
        let rules =
          is_required == 1
            ? "required|file-image-pdf|file-size"
            : "file-image-pdf|file-size";
        let file = vm.filesList.find((item) => {
          return item.file_upload_id == file_upload_id;
        });
        let url = null;
        let hospital_license_fileupload_mapping_id = "";
        let radioValue = "";
        if (file instanceof Object) {
          url = file.file_name;
          hospital_license_fileupload_mapping_id =
            file.hospital_license_fileupload_mapping_id;
          // For radio button type, file_name stores the yes/no value
          if (fieldtype === 2) {
            radioValue = file.file_name || "";
          }
        }
        return {
          name: file_upload_name,
          file_upload_id,
          file: "",
          url,
          hospital_license_fileupload_mapping_id,
          rules,
          fieldtype: fieldtype || 1,
          radioValue,
        };
      });
    },
    inputRadio() {
      const vm = this;
      this.$emit("input", vm.files);
    },
    input() {
      // let files = vm.files;
      // let item = files[i];
      // if (item.file !== null) {
      //   let types = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      //   let { type, size } = item.file;
      //   size = size / 1048576;
      //   if (size > 5) {
      //     files[i].file = "";
      //     vm.$alert.show("File size must be less then 5mb");
      //   } else if (types.indexOf(type) == -1) {
      //     files[i].file = "";
      //     vm.$alert.show("File type not allowed");
      //   }
      // }
      // vm.files = files;
      this.$emit("input", this.files);
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style></style>
